import React, { useState, useEffect, useMemo } from 'react';
import { UserRole } from '../types';
import { ChevronRight, X, Check, ArrowRight } from 'lucide-react';

interface DemoWalkthroughProps {
  role: UserRole;
  onDismiss: () => void;
}

interface Step {
  targetId?: string; // ID of the element to highlight. If undefined, it's a center modal.
  title: string;
  content: string;
  position?: 'top' | 'bottom' | 'left' | 'right' | 'center';
  actionLabel?: string;
  showSkip?: boolean;
}

export const DemoWalkthrough: React.FC<DemoWalkthroughProps> = ({ role, onDismiss }) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [highlightRect, setHighlightRect] = useState<DOMRect | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  const steps: Step[] = useMemo(() => {
    if (role === UserRole.PROVIDER) {
      return [
        {
          title: "Welcome to Verline Provider",
          content: "This is your command center. Verline is designed to help providers manage multiple receivers, projects, and teams in one unified workspace.",
          position: 'center',
          actionLabel: "Start Tour"
        },
        {
          targetId: 'workspace-trigger',
          title: "Multi-Workspace Architecture",
          content: "Switch between your Provider HQ and different Receiver Workspaces here. Keep data separate but accessible.",
          position: 'bottom',
        },
        {
          targetId: 'nav-item-projects',
          title: "Project Management",
          content: "Track all active projects, milestones, and deliverables across all your receivers in one view.",
          position: 'right',
        },
        {
          targetId: 'dashboard-new-project-btn',
          title: "Quick Launch",
          content: "Spin up new receiver projects in seconds. Preset milestones and automated onboarding emails make kickoff a breeze.",
          position: 'bottom',
        },
        {
          targetId: 'nav-item-inbox',
          title: "Unified Inbox",
          content: "Stop chasing emails. All receiver approvals, feedback, and milestone sign-offs land right here.",
          position: 'right',
        },
        {
          targetId: 'nav-item-people',
          title: "People & Permissions",
          content: "Manage your team and invite receivers. Control exactly what they see and do.",
          position: 'right',
        },
        {
          title: "You're Ready!",
          content: "Feel free to explore. Creating projects and switching roles is safe in this demo environment.",
          position: 'center',
          actionLabel: "Let's Go",
          showSkip: false
        }
      ];
    } else {
      return [
        {
          title: "Welcome to the Receiver Portal",
          content: "Experience what your receivers will see. Transparency build trust, and Verline makes it automatic.",
          position: 'center',
          actionLabel: "Show Me Around"
        },
        {
          targetId: 'nav-item-projects',
          title: "Real-time Progress",
          content: "Receivers can see exactly where their project stands, what's done, and what's next, without emailing you.",
          position: 'right',
        },
        {
          targetId: 'nav-item-inbox',
          title: "Action Items",
          content: "When you need approval on a design or document, it appears here for them to review with one click.",
          position: 'right',
        },
        {
          title: "Seamless Collaboration",
          content: "No more lost files or buried email threads. Everything stays in context.",
          position: 'center',
          actionLabel: "Explore Receiver View",
          showSkip: false
        }
      ];
    }
  }, [role]);

  const currentStep = steps[currentStepIndex];

  // Effect to find target element and update highlight rect
  useEffect(() => {
    const updateRect = () => {
      // slight delay to ensure UI is ready
      setTimeout(() => {
        if (currentStep.targetId) {
          const el = document.getElementById(currentStep.targetId);
          if (el) {
            const rect = el.getBoundingClientRect();
            setHighlightRect(rect);
          } else {
            // Fallback to center if element not found (e.g. mobile view or navigation mismatch)
            setHighlightRect(null);
          }
        } else {
          setHighlightRect(null);
        }
        setIsVisible(true);
      }, 150);
    };

    updateRect();
    window.addEventListener('resize', updateRect);
    return () => window.removeEventListener('resize', updateRect);
  }, [currentStepIndex, currentStep, role]);

  const handleNext = () => {
    if (currentStepIndex < steps.length - 1) {
      setIsVisible(false); // Fade out briefly
      setTimeout(() => setCurrentStepIndex(prev => prev + 1), 200);
    } else {
      onDismiss();
    }
  };

  const styles = {
    popover: (rect: DOMRect | null, position: string) => {
      const baseOffset = 16;
      if (!rect) return {}; // Center handled by class

      let top = 0;
      let left = 0;
      const popoverWidth = 340; // Defined in JSX
      const windowWidth = window.innerWidth;

      switch (position) {
        case 'right':
          top = rect.top + (rect.height / 2) - 100; // rough generic drift correction
          left = rect.right + baseOffset;
          break;
        case 'left':
          top = rect.top;
          left = rect.left - popoverWidth - baseOffset;
          break;
        case 'bottom':
          top = rect.bottom + baseOffset;
          left = rect.left;
          break;
        case 'top':
          top = rect.top - 200 - baseOffset;
          left = rect.left;
          break;
        default:
          break;
      }

      // Boundary checks
      if (top < 10) top = 10;
      if (left < 10) left = 10;

      // Right edge check
      if (left + popoverWidth > windowWidth - 10) {
        left = windowWidth - popoverWidth - 10;
      }

      return { top, left, position: 'absolute' as const };
    }
  };

  if (!steps.length) return null;

  return (
    <div className="fixed inset-0 z-[100] overflow-hidden pointer-events-none">
      {/* Backdrop Mask - complex implementation simplified with a full overlay and clip-path or multiple divs. 
                For simplicity and performance, we'll use a dark overlay with a "hole" using mix-blend-mode or just absolute positioning 4 divs around the rect.
                Actually, simpler: SVG overlay.
            */}

      <div className={`absolute inset-0 bg-black/50 transition-opacity duration-300 pointer-events-auto ${highlightRect ? 'opacity-0' : (isVisible ? 'opacity-100' : 'opacity-0')}`} />

      {/* Highlight Hole logic */}
      {highlightRect && (
        <div
          className="absolute shadow-[0_0_0_9999px_rgba(0,0,0,0.75)] rounded-md transition-all duration-300 ease-in-out pointer-events-none border-2 border-emerald-500/50"
          style={{
            top: highlightRect.top - 4,
            left: highlightRect.left - 4,
            width: highlightRect.width + 8,
            height: highlightRect.height + 8,
            zIndex: 101
          }}
        />
      )}

      {/* Popover */}
      <div
        className={`pointer-events-auto absolute transition-all duration-300 ease-out flex flex-col items-start z-[102] ${currentStep.targetId ? '' : 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'}`}
        style={currentStep.targetId && highlightRect ? styles.popover(highlightRect, currentStep.position || 'bottom') : {}}
      >
        <div
          className={`
                        w-[340px] bg-white dark:bg-zinc-900 rounded-xl shadow-2xl border border-zinc-200 dark:border-zinc-800 p-6 relative overflow-hidden
                        transform transition-all duration-300 ${isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-4 scale-95'}
                    `}
        >
          {/* Decorative gradient */}
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-500 to-teal-400"></div>

          {/* Step Counter */}
          <div className="flex items-center justify-between mb-4">
            <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">
              Step {currentStepIndex + 1} of {steps.length}
            </span>
            {currentStep.showSkip !== false && (
              <button
                onClick={onDismiss}
                className="text-[11px] text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors"
              >
                Skip Tour
              </button>
            )}
          </div>

          <h3 className="text-lg font-bold text-zinc-900 dark:text-zinc-100 mb-2 leading-tight">
            {currentStep.title}
          </h3>

          <p className="text-[14px] text-zinc-600 dark:text-zinc-400 leading-relaxed mb-6">
            {currentStep.content}
          </p>

          <div className="flex items-center justify-between mt-auto">
            <div className="flex gap-1.5">
              {steps.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${idx === currentStepIndex ? 'bg-emerald-500 w-4' : 'bg-zinc-200 dark:bg-zinc-800'}`}
                />
              ))}
            </div>

            <button
              onClick={handleNext}
              className="flex items-center gap-2 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 px-4 py-2 rounded-lg text-[13px] font-medium hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-all hover:scale-105 active:scale-95 shadow-md shadow-zinc-500/10"
            >
              {currentStep.actionLabel || (currentStepIndex === steps.length - 1 ? "Finish" : "Next")}
              {currentStepIndex < steps.length - 1 && <ArrowRight size={14} />}
              {currentStepIndex === steps.length - 1 && <Check size={14} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
